import { useCreateBlockNote } from '@blocknote/react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { presentationToBlockNoteAdapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/presentationToBlocknote.adapter'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { blocknoteSchema } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/blocknoteSchema'
import { useDebouncedCallback } from 'use-debounce'
import {
  hasPresentationChanged,
  mergeEditorPresentationIntoFullPresentation
} from '../utils/presentation'
import { uploadFile } from '../utils/uploadFile'
import { resolveFileUrl } from '../utils/resolveFileUrl'
import { limitPhaseBlockCount } from './utils/limitPhaseBlockCount'
import { getVariableValue } from '../utils/quoteVariables'
import { updateQuoteVariableText } from './QuoteVariables/helpers'
import { QuoteVariableType } from '@sequencehq/quote-content'
import { createExtension } from '@blocknote/core'
import { realignJustifiedText } from './utils/realignJustifiedText'

type useContentEditor = () => {
  features: {
    contentEditor: {
      editor: typeof blocknoteSchema.BlockNoteEditor
      onChange: () => void
      onDragEnd: () => void
      disabled: boolean
      addPhaseBlock: {
        enabled: boolean
      }
    }
    quoteVariables: {
      values: Record<QuoteVariableType, string | undefined>
    }
  }
}

/**
 * We only support indentation for list items. This extension
 * disables the indentation behaviour for other block types.
 */
const blockTabExtension = createExtension({
  key: 'block-tab-for-non-list',
  keyboardShortcuts: {
    Tab: ({ editor }) => {
      const cursorPosition = editor.getTextCursorPosition()

      /**
       * Return true to block identation.
       */
      if (!cursorPosition) return true

      const block = editor.getBlock(cursorPosition.block)

      /**
       * Returning false allows the default behaviour to continue.
       */
      if (
        block &&
        ['bulletListItem', 'numberedListItem', 'table'].includes(block.type)
      ) {
        return false
      }

      return true
    }
  }
})

export const useContentEditor: useContentEditor = () => {
  const cubeContext = useCubeContext()
  const blockNoteEditor = useCreateBlockNote(
    {
      schema: blocknoteSchema,
      initialContent: presentationToBlockNoteAdapter.in(
        cubeContext.queries.rawData.data.presentation
      ),
      uploadFile: uploadFile,
      resolveFileUrl: resolveFileUrl,
      tabBehavior: 'prefer-navigate-ui',
      extensions: [
        /**
         * We use this custom extension to prevent the default tab behaviour
         * (indentation) since we don't support it in our editor.
         */
        blockTabExtension
      ],
      /**
       * Optional features for table blocks
       */
      tables: {
        splitCells: false, // difficult to render in a quote PDF
        cellBackgroundColor: true,
        cellTextColor: true,
        headers: true
      }
    },
    [cubeContext.queries.rawData.editor.lastLoadedAt]
  )

  const previousBlockNoteDocument = useRef(blockNoteEditor.document)
  useEffect(() => {
    previousBlockNoteDocument.current = blockNoteEditor.document
  }, [blockNoteEditor.document])

  const syncPresentationToDomain = useDebouncedCallback(() => {
    const blockNotePresentation = presentationToBlockNoteAdapter.out({
      cubeQueries: cubeContext.queries
    })(blockNoteEditor.document)

    /**
     * Don't push changes to presentation if the editor is in an uneditable state anyway
     */
    if (!cubeContext.queries.availableFeatures.quote.edit.available.enabled) {
      return
    }

    const newPresentation = mergeEditorPresentationIntoFullPresentation(
      blockNotePresentation,
      cubeContext.queries.rawData.data.presentation
    )

    if (
      !hasPresentationChanged(
        cubeContext.queries.rawData.data.presentation,
        newPresentation
      )
    ) {
      return
    }

    cubeContext.mutators.updateData({
      presentation: newPresentation
    })
  }, 500)

  const onDragEnd = useCallback(() => {
    /**
     * Giving a phase block a fixed height while it's being dragged (see BlockNoteOverrides.scss)
     * seems to improve reliability. When a block is being dragged, its outer container has the attribute
     * draggable=true. It doesn't seem to reset after the drag is complete, so we set the text cursor position to
     * the phase block, which removes the draggable attribute from the block.
     */
    const element = document.querySelector(
      '.bn-block-outer[draggable="true"]:has(.node-phases)'
    )
    if (!element) return

    const phaseBlock = blockNoteEditor.document.find(
      block => block.type === 'phases'
    )
    if (!phaseBlock) return

    blockNoteEditor.setTextCursorPosition(phaseBlock)
  }, [blockNoteEditor])

  const addPhaseBlockEnabled: boolean = useMemo(() => {
    return !cubeContext.queries.rawData.data.presentation.blocks.find(
      block => block.blockType === 'phases'
    )
  }, [cubeContext.queries.rawData.data.presentation])

  const contentEditorFeature = useMemo(() => {
    return {
      editor: blockNoteEditor,
      disabled:
        !cubeContext.queries.availableFeatures.quote.edit.available.enabled,
      onChange: () => {
        realignJustifiedText(blockNoteEditor)
        limitPhaseBlockCount({ previousBlockNoteDocument, blockNoteEditor })
        syncPresentationToDomain()
      },
      onDragEnd,
      addPhaseBlock: {
        enabled: addPhaseBlockEnabled
      }
    }
  }, [
    blockNoteEditor,
    addPhaseBlockEnabled,
    cubeContext.queries.availableFeatures.quote.edit.available.enabled,
    syncPresentationToDomain,
    onDragEnd,
    blockNoteEditor.document
  ])

  // biome-ignore lint/correctness/useExhaustiveDependencies: only updated when specific queries change
  const quoteVariablesFeature = useMemo(() => {
    const variableValue = getVariableValue(
      cubeContext.queries,
      cubeContext.queries.rawData.data
    )

    return {
      values: {
        CURRENCY: variableValue('CURRENCY'),
        CUSTOMER_NAME: variableValue('CUSTOMER_NAME'),
        DURATION: variableValue('DURATION'),
        START_DATE: variableValue('START_DATE'),
        END_DATE: variableValue('END_DATE'),
        CUSTOMER_ADDRESS: variableValue('CUSTOMER_ADDRESS'),
        CUSTOMER_COUNTRY: variableValue('CUSTOMER_COUNTRY'),
        QUOTE_OWNER: variableValue('QUOTE_OWNER'),
        PAYMENT_TERMS: variableValue('PAYMENT_TERMS'),
        RECIPIENTS: variableValue('RECIPIENTS')
      }
    }
  }, [
    JSON.stringify({
      totalPricingDuration: cubeContext.queries.totalPricingDuration,
      currency: cubeContext.queries.rawData.data.common.currency,
      customerId: cubeContext.queries.rawData.data.common.customerId,
      startDate: cubeContext.queries.rawData.data.common.startDate,
      defaultDueDateDays:
        cubeContext.queries.rawData.data.quote.defaultDueDateDays,
      createdByEmail: cubeContext.queries.rawData.data.quote.createdByEmail,
      contacts: cubeContext.queries.rawData.data.quote.contacts
    })
  ])

  const features = useMemo(() => {
    return {
      contentEditor: contentEditorFeature,
      quoteVariables: quoteVariablesFeature
    }
  }, [contentEditorFeature, quoteVariablesFeature])

  // biome-ignore lint/correctness/useExhaustiveDependencies: only update when variables change
  useEffect(() => {
    updateQuoteVariableText(blockNoteEditor, quoteVariablesFeature.values)
  }, [quoteVariablesFeature.values])

  useEffect(() => {
    syncPresentationToDomain()
  }, [blockNoteEditor.document, syncPresentationToDomain])

  useEffect(() => {
    /**
     * If we just reinitialized, cancel any queued debounced updates, since
     * the fresh data is now the ground truth. Ordering matters here - it
     * must be placed after the useEffect which calls the syncPresentationToDomain
     * function, otherwise a sneaky extra call will happen after the cancel.
     */
    syncPresentationToDomain.cancel()
  }, [
    cubeContext.queries.rawData.editor.lastLoadedAt,
    syncPresentationToDomain
  ])

  return {
    features
  }
}
